<script lang="ts" setup>
import arrowDown from "@/assets/image/icons/arrow-down.svg";
import type { TServicescarouselItem } from "@/assets/ts/globalTypes";

withDefaults(
  defineProps<{
    carousel: TServicescarouselItem[];
  }>(),
  {}
);

const scrollerRef = ref<HTMLDivElement | null>(null);
const canLeft = ref(false);
const canRight = ref(false);
let step = 0;

const computeStep = () => {
  const el = scrollerRef.value;
  if (!el) return;
  const first = el.querySelector<HTMLElement>(".carousel-item");
  const gapStr = getComputedStyle(el).columnGap || getComputedStyle(el).gap || "0";
  const gap = Number.parseFloat(gapStr) || 0;
  step = first ? Math.round(first.getBoundingClientRect().width + gap) : Math.round(el.clientWidth * 0.9);
};

const updateButtons = () => {
  const el = scrollerRef.value;
  if (!el) return;
  canLeft.value = el.scrollLeft > 0;
  canRight.value = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;
};

const scrollByAmt = (dir: 1 | -1) => {
  const el = scrollerRef.value;
  if (!el) return;
  el.scrollBy({ left: dir * step, behavior: "smooth" });
  setTimeout(updateButtons, 300);
};

const prev = () => scrollByAmt(-1);
const next = () => scrollByAmt(1);

const onResize = () => {
  computeStep();
  updateButtons();
};
const onScroll = () => updateButtons();

onMounted(() => {
  computeStep();
  updateButtons();
  scrollerRef.value?.addEventListener("scroll", onScroll);
  window.addEventListener("resize", onResize);
});
onBeforeUnmount(() => {
  scrollerRef.value?.removeEventListener("scroll", onScroll);
  window.removeEventListener("resize", onResize);
});
</script>

<template>
  <div class="relative w-full overflow-hidden">
    <!-- Scroller -->
    <div ref="scrollerRef" class="flex gap-4 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <services-image-card
        v-for="(g, i) in carousel"
        :key="i"
        :text="g.text"
        :image="g.image"
        class="carousel-item h-[305px] w-[305px] flex-shrink-0"
      />
    </div>

    <!-- Left -->
    <button
      v-show="canLeft"
      class="bg-text-dark absolute top-0 left-0 z-10 flex h-full w-[32px] cursor-pointer items-center justify-center opacity-80 transition-opacity duration-300 hover:opacity-100"
      aria-label="previous"
      @click="prev"
    >
      <img :src="arrowDown" alt="arrow left" class="rotate-90" />
    </button>

    <!-- Right -->
    <button
      v-show="canRight"
      class="bg-text-dark absolute top-0 right-0 z-10 flex h-full w-[32px] cursor-pointer items-center justify-center opacity-80 transition-opacity duration-300 hover:opacity-100"
      aria-label="next"
      @click="next"
    >
      <img :src="arrowDown" alt="arrow right" class="rotate-270" />
    </button>
  </div>
</template>
